import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Search, Check, X } from "lucide-react";

const CLASSES = [
  { key: "",         label: "UNCLASSIFIED", hint: "Clears the class · falls back to default pricing" },
  { key: "A",        label: "CLASS A",       hint: "Premium tier · account" },
  { key: "B",        label: "CLASS B",       hint: "Standard tier · account" },
  { key: "C",        label: "CLASS C",       hint: "Discount tier · account" },
  { key: "CASH",     label: "CASH / EFT",    hint: "Walk-in · same rate for Cash & EFT" },
  { key: "CONTRACT", label: "CONTRACT",      hint: "Negotiated · account" },
];

export default function BulkClassifyDialog({ open, onOpenChange, customers, onDone }) {
  const [target, setTarget] = useState("A");
  const [q, setQ] = useState("");
  const [onlyUncl, setOnlyUncl] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTarget("A");
      setQ("");
      setOnlyUncl(true);
      setSelected(new Set());
    }
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (customers || []).filter((c) => {
      if (onlyUncl && (c.pricing_class || "")) return false;
      if (!needle) return true;
      return (
        (c.name || "").toLowerCase().includes(needle) ||
        (c.abn || "").toLowerCase().includes(needle)
      );
    });
  }, [customers, q, onlyUncl]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (filtered.every((c) => selected.has(c.id))) {
      const next = new Set(selected);
      filtered.forEach((c) => next.delete(c.id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      filtered.forEach((c) => next.add(c.id));
      setSelected(next);
    }
  };

  const apply = async () => {
    const ids = [...selected];
    if (ids.length === 0) {
      toast.error("Select at least one customer");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/customers/bulk-classify", {
        customer_ids: ids,
        pricing_class: target,
      });
      toast.success(`Classified ${res.data.updated} customer${res.data.updated === 1 ? "" : "s"} as ${target || "UNCLASSIFIED"}`);
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to bulk classify");
    } finally {
      setSaving(false);
    }
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="bulk-classify-dialog"
        className="max-w-2xl rounded-none bg-surface border-industrial text-ink"
      >
        <DialogHeader>
          <DialogTitle className="uppercase tracking-widest text-primary-orange inline-flex items-center gap-2">
            <Users className="w-4 h-4" /> Bulk classify customers
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <div className="label-tag mb-1">Target class</div>
            <div className="grid grid-cols-3 gap-1.5">
              {CLASSES.map((c) => (
                <button
                  key={c.key}
                  data-testid={`bulk-target-${c.key || "uncl"}`}
                  type="button"
                  onClick={() => setTarget(c.key)}
                  title={c.hint}
                  className={`text-left px-2.5 py-1.5 border transition-colors ${target === c.key ? "border-primary-orange bg-primary-orange/10 text-primary-orange" : "border-industrial text-muted-industrial hover:text-ink"}`}
                >
                  <div className="mono text-[11px] uppercase font-bold">{c.label}</div>
                  <div className="text-[10px] text-muted-industrial">{c.hint}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-industrial" />
              <Input
                data-testid="bulk-classify-search"
                placeholder="Search name or ABN…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="rounded-none bg-transparent border-industrial mono text-xs pl-7 h-8"
              />
            </div>
            <label className="inline-flex items-center gap-1.5 text-[11px] mono cursor-pointer" data-testid="bulk-only-uncl">
              <input type="checkbox" checked={onlyUncl} onChange={(e) => setOnlyUncl(e.target.checked)} className="w-3.5 h-3.5 accent-[rgb(var(--primary))]" />
              UNCL only
            </label>
          </div>

          <div className="border border-industrial max-h-[320px] overflow-y-auto">
            <div className="flex items-center justify-between px-2 py-1.5 border-b border-industrial bg-surface-2 sticky top-0">
              <button
                data-testid="bulk-toggle-all"
                type="button"
                onClick={toggleAll}
                className="mono text-[10px] uppercase tracking-widest text-primary-orange hover:underline inline-flex items-center gap-1"
              >
                {allFilteredSelected ? <X className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                {allFilteredSelected ? "Deselect" : "Select"} all {filtered.length}
              </button>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">
                Selected: <span className="text-ink font-bold">{selected.size}</span>
              </div>
            </div>
            {filtered.length === 0 && (
              <div className="text-xs mono text-muted-industrial p-3 text-center">
                No customers match this filter.
              </div>
            )}
            {filtered.map((c) => {
              const on = selected.has(c.id);
              const cls = (c.pricing_class || "").toUpperCase();
              return (
                <label
                  key={c.id}
                  data-testid={`bulk-classify-row-${c.id}`}
                  className={`flex items-center gap-2 border-b border-industrial/60 px-2 py-1.5 text-[11px] cursor-pointer ${on ? "bg-primary-orange/5" : "hover:bg-surface-2"}`}
                >
                  <input type="checkbox" checked={on} onChange={() => toggle(c.id)} className="w-3.5 h-3.5 accent-[rgb(var(--primary))]" />
                  <div className="flex-1 min-w-0">
                    <div className="mono font-bold truncate">{c.name}</div>
                    <div className="mono text-[10px] text-muted-industrial truncate">{c.abn || "—"}</div>
                  </div>
                  <span className={`mono text-[10px] uppercase px-1.5 py-0.5 border ${cls ? "border-industrial text-muted-industrial" : "border-red-500/60 text-red-500 bg-red-500/10 font-bold"}`}>
                    {cls || "UNCL."}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-none border-industrial uppercase tracking-widest text-xs"
          >
            Cancel
          </Button>
          <Button
            data-testid="bulk-classify-apply"
            onClick={apply}
            disabled={saving || selected.size === 0}
            className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold disabled:opacity-40"
          >
            {saving ? "Applying…" : `Apply ${target || "UNCL"} to ${selected.size}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
